import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/api/axios';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { cn } from '@/components/ui/utils';

const { FiX, FiLayers, FiCalendar, FiImage, FiCheck, FiInfo, FiActivity, FiUploadCloud, FiTrash2 } = FiIcons;

const SchemeModal = ({ isOpen, onClose, scheme = null }) => {
  const queryClient = useQueryClient();
  const isEdit = !!scheme;
  const fileInputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isConverting, setIsConverting] = useState(false);

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      scheme_name: '',
      brand_name: '',
      campaign_image: '',
      status: 'active',
      start_date: '',
      end_date: '',
    }
  });

  useEffect(() => {
    if (scheme) {
      reset({
        scheme_id: scheme.id,
        scheme_name: scheme.scheme_name || '',
        brand_name: scheme.brand_name || '',
        campaign_image: scheme.campaign_image || '',
        status: scheme.status || 'active',
        start_date: scheme.start_date || '',
        end_date: scheme.end_date || '',
      });
      setPreviewUrl(scheme.campaign_image || '');
    } else {
      reset({
        scheme_name: '',
        brand_name: '',
        campaign_image: '',
        status: 'active',
        start_date: '',
        end_date: '',
      });
      setPreviewUrl('');
    }
  }, [scheme, reset, isOpen]);

  const mutation = useMutation({
    mutationFn: (data) => api.post('save_scheme.php', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schemes'] });
      onClose();
    },
    onError: (error) => {
      alert(error.response?.data?.message || 'Failed to save scheme');
    }
  });

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsConverting(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setPreviewUrl(base64String);
        setValue('campaign_image', base64String);
        setIsConverting(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (e) => {
    e.stopPropagation();
    setPreviewUrl('');
    setValue('campaign_image', '');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onSubmit = (data) => {
    // Ensure scheme_id is an integer if present
    const payload = {
      ...data,
      scheme_id: data.scheme_id ? parseInt(data.scheme_id) : undefined
    };
    mutation.mutate(payload);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-surface-950/40 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white rounded-[2rem] w-full max-w-xl overflow-hidden shadow-2xl border border-surface-200"
      >
        <div className="px-6 py-5 border-b border-surface-100 flex items-center justify-between bg-surface-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-600 flex items-center justify-center text-white shadow-lg shadow-primary-100">
              <SafeIcon icon={FiLayers} className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-black text-surface-900 leading-none">{isEdit ? 'Update Campaign' : 'New Campaign'}</h3>
              <p className="text-[10px] text-surface-400 font-bold mt-1 uppercase tracking-wider">
                {isEdit ? `ID: ${scheme.id}` : 'Fill in campaign details'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white rounded-lg transition-colors border border-transparent hover:border-surface-200">
            <SafeIcon icon={FiX} className="w-4 h-4 text-surface-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-surface-400 uppercase tracking-widest ml-1">Campaign Name *</label>
              <Input {...register('scheme_name', { required: 'Required' })} placeholder="Summer 2024" className="h-11 rounded-xl bg-surface-50 border-transparent focus:bg-white" />
              {errors.scheme_name && <p className="text-[9px] font-bold text-red-500 ml-1">{errors.scheme_name.message}</p>}
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-surface-400 uppercase tracking-widest ml-1">Brand Name</label>
              <Input {...register('brand_name')} placeholder="CISPL Global" className="h-11 rounded-xl bg-surface-50 border-transparent focus:bg-white" />
            </div>

            {/* Minimalist Upload Section */}
            <div className="md:col-span-2 space-y-1.5">
              <label className="text-[10px] font-black text-surface-400 uppercase tracking-widest ml-1">Campaign Banner</label>
              <div 
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  "flex items-center gap-4 p-3 rounded-xl border-2 border-dashed transition-all cursor-pointer group",
                  previewUrl ? "border-primary-100 bg-primary-50/30" : "border-surface-200 hover:border-primary-300 hover:bg-surface-50"
                )}
              >
                <div className="w-12 h-12 rounded-lg bg-white border border-surface-200 flex items-center justify-center shrink-0 overflow-hidden relative">
                  {previewUrl ? (
                    <img src={previewUrl} className="w-full h-full object-cover" alt="Preview" />
                  ) : (
                    <SafeIcon icon={FiImage} className="w-5 h-5 text-surface-300" />
                  )}
                  {isConverting && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-surface-700 truncate">
                    {previewUrl ? 'Image Selected' : 'Click to upload banner'}
                  </p>
                  <p className="text-[10px] text-surface-400 font-medium">JPG, PNG allowed</p>
                </div>
                {previewUrl && (
                  <button 
                    type="button" 
                    onClick={removeImage}
                    className="p-2 hover:bg-red-100 rounded-lg text-red-500 transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-surface-400 uppercase tracking-widest ml-1">Start Date</label>
              <Input type="date" {...register('start_date')} className="h-11 rounded-xl bg-surface-50 border-transparent focus:bg-white" />
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-surface-400 uppercase tracking-widest ml-1">End Date</label>
              <Input type="date" {...register('end_date')} className="h-11 rounded-xl bg-surface-50 border-transparent focus:bg-white" />
            </div>

            <div className="md:col-span-2 flex gap-3">
              {['active', 'inactive'].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setValue('status', s)}
                  className={cn(
                    "flex-1 py-2.5 rounded-xl border-2 text-[10px] font-black uppercase tracking-widest transition-all",
                    watch('status') === s 
                      ? "border-primary-500 bg-primary-50 text-primary-700" 
                      : "border-surface-100 bg-white text-surface-400 hover:border-surface-200"
                  )}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </form>

        <div className="px-6 py-5 bg-surface-50 border-t border-surface-100 flex items-center justify-end gap-3">
          <Button variant="ghost" type="button" onClick={onClose} className="h-10 px-6 rounded-xl font-bold text-xs">Cancel</Button>
          <Button 
            onClick={handleSubmit(onSubmit)}
            disabled={mutation.isPending || isConverting} 
            className="h-10 px-8 rounded-xl bg-surface-900 text-white"
          >
            {mutation.isPending ? 'Saving...' : (
              <span className="font-black uppercase text-[10px] tracking-widest">
                {isEdit ? 'Update' : 'Create'} Campaign
              </span>
            )}
          </Button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

export default SchemeModal;